import React, { useState, Fragment, useEffect } from "react";
import { Button, Divider, Icon, Modal, Upload, List, Typography, message, Radio } from "antd";
import { useTranslation } from "react-i18next";
import Input from "../../../../common/component/Input";
import { checkUndefine, capitalizeFirstLetter } from "../../../../../../helpers/functions";
import './GuestEmails.less';
import { LoadingComponent } from "../../../../common/component/Index";
import Wrapper from "../../../../common/component/Wrapper";
import { CAMPAIGN } from "../../../../../../constants/Define";
import { setListRates } from "../../../../../../actions/rateAction";

const EMAIL_PATTERN = /^[\w-.]+@([\w-]+\.)+[\w-]+$/;
const EMAIL_SUFFIX_PATTERN = /^@([\w-]+\.)+[\w-]+$/;

const GuestEmails = ({ ...props }) => {
    const { t } = useTranslation();
    const { onChange, lstEmails, lstEmailSuffix, campaignState, parseExcel, canEdit, parseExcelClearData, buttonText } = props;
    const { isFetchParseExcel, emailDetail } = campaignState;
    const [visible, setVisible] = useState(false);
    const [emailInput, setEmailInput] = useState('');
    const [emails, setEmails] = useState([]);
    const [emailSuffix, setEmailSuffix] = useState([]);
    const [emailError, setEmailError] = useState({});
    const [typeData, setTypeData]  = useState('')

    useEffect(() => {
        if (emailDetail && emailDetail.data && emailDetail.errors) {
            setEmails([...emailDetail.data]);
            setEmailError({
                validationError: emailDetail.validationError,
                errors: emailDetail.errors,
            });
        }
    }, [emailDetail]);

    useEffect(() => {
        if(campaignState.detail.campaign.objectTypeDataEmail !== CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.SPECIFIC.id && campaignState.detail.campaign.objectTypeDataEmail !== CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.EMAIL_SUFFIX.id) {
            setTypeData(CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.SPECIFIC.id)
        } else {
            setTypeData(campaignState.detail.campaign.objectTypeDataEmail)
        }
    }, [campaignState.detail.campaign.objectTypeDataEmail])

    useEffect(() => {
        setEmails(lstEmails);
    }, [lstEmails]);

    useEffect(() => {
        setEmailSuffix(lstEmailSuffix);
    }, [lstEmailSuffix]);

    const showModal = () => {
        setVisible(true);
    };

    const handleOk = () => {
        if(onChange) {
            typeData === CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.SPECIFIC.id ? onChange('emails', emails) : onChange('emailSuffix', emailSuffix)
            onChange('objectTypeDataEmail', typeData)
        }
        clearData();
    };

    const handleCancel = () => {
        setEmails(lstEmails);
        setEmailSuffix(lstEmailSuffix);
        clearData();
    };

    const clearData = () => {
        setVisible(false);
        setEmailInput('');
        setEmailError({});
        parseExcelClearData && parseExcelClearData();
    }

    const onChangeValue = (e) => {
        setEmailInput(e.target.value);
    };

    const showValidationWarning = (invalidValues = [], duplicateValues = [], invalidKey, duplicateKey) => {
        const warningMessages = [];

        if (invalidValues.length > 0) {
            warningMessages.push(
                t(invalidKey, { values: invalidValues.join(', ') })
            );
        }

        if (duplicateValues.length > 0) {
            warningMessages.push(
                t(duplicateKey, { values: duplicateValues.join(', ') })
            );
        }

        if (warningMessages.length > 0) {
            message.warning({
                content: warningMessages.join(' '),
            });
        }
    }

    const addSpecificEmailsToList = () => {
        const oldEmails = [...emails];
        const normalizedExistedEmails = new Set(
            oldEmails.map(item => item.trim().toLowerCase())
        );
        const invalidEmails = [];
        const duplicateEmails = [];
        const validEmails = [];
        const inputEmails = emailInput
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);

        if (inputEmails.length <= 0) {
            message.warning({
                content: t(`item.campaign.emailNotValid`),
            });
            return;
        }

        inputEmails.forEach(item => {
            const normalizedItem = item.toLowerCase();

            if (!EMAIL_PATTERN.test(normalizedItem)) {
                invalidEmails.push(item);
                return;
            }

            if (normalizedExistedEmails.has(normalizedItem)) {
                duplicateEmails.push(item);
                return;
            }

            normalizedExistedEmails.add(normalizedItem);
            validEmails.push(normalizedItem);
        });

        const hasErrors = invalidEmails.length > 0 || duplicateEmails.length > 0;

        if (validEmails.length > 0) {
            setEmails([...validEmails, ...oldEmails]);
        }

        if (!hasErrors && validEmails.length > 0) {
            setEmailInput('');
        }

        showValidationWarning(
            invalidEmails,
            duplicateEmails,
            `item.campaign.emailInvalid`,
            `item.campaign.emailDuplicated`
        );
    }

    const addEmailSuffixesToList = () => {
        const oldEmailSuffixes = [...emailSuffix];
        const normalizedExistedEmailSuffixes = new Set(
            oldEmailSuffixes.map(item => item.trim().toLowerCase())
        );
        const invalidEmailSuffixes = [];
        const duplicateEmailSuffixes = [];
        const validEmailSuffixes = [];
        const inputEmailSuffixes = emailInput
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);

        if (inputEmailSuffixes.length <= 0) {
            message.warning({
                content: t(`item.campaign.emailNotValid`),
            });
            return;
        }

        inputEmailSuffixes.forEach(item => {
            const normalizedItem = item.toLowerCase();

            if (!EMAIL_SUFFIX_PATTERN.test(normalizedItem)) {
                invalidEmailSuffixes.push(item);
                return;
            }

            if (normalizedExistedEmailSuffixes.has(normalizedItem)) {
                duplicateEmailSuffixes.push(item);
                return;
            }

            normalizedExistedEmailSuffixes.add(normalizedItem);
            validEmailSuffixes.push(normalizedItem);
        });

        const hasErrors = invalidEmailSuffixes.length > 0 || duplicateEmailSuffixes.length > 0;

        if (validEmailSuffixes.length > 0) {
            setEmailSuffix([...validEmailSuffixes, ...oldEmailSuffixes]);
        }

        if (!hasErrors && validEmailSuffixes.length > 0) {
            setEmailInput('');
        }

        showValidationWarning(
            invalidEmailSuffixes,
            duplicateEmailSuffixes,
            `item.campaign.emailSuffixInvalid`,
            `item.campaign.emailSuffixDuplicated`
        );
    }

    const addEmailToList = () => {
        if (typeData === CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.SPECIFIC.id) {
            addSpecificEmailsToList();
            return;
        }

        if (typeData === CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.EMAIL_SUFFIX.id) {
            addEmailSuffixesToList();
            return;
        }
    }

    const handleChange = (file) => {
        parseExcel && parseExcel(file.file)
    }

    const onRemoveEmail = (item) => {
        if (item) {
            const oldEmails = typeData === CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.SPECIFIC.id ? [...emails] : [...emailSuffix];
            const newEmails = oldEmails.filter(eleItem => {
                return eleItem !== item;
            });
            // setEmails([...newEmails]);
            const funcSetEmail = typeData === CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.SPECIFIC.id ? setEmails : setEmailSuffix
            funcSetEmail([...newEmails])
        }
    }

    const renderButton = () => {
      if (buttonText) {
        return (
          <Button type="primary" onClick={ showModal }>
            {buttonText}
          </Button>
        )
      }
      return (<Button type="primary" onClick={ showModal }>
        { 
          typeData === CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.SPECIFIC.id ?
          (lstEmails && lstEmails.length > 0 ? t(`item.campaign.selectedEmail`).replace('{0}', lstEmails.length) : t(`item.campaign.selectEmail`) ) :
          (lstEmailSuffix && lstEmailSuffix.length > 0 ? t(`item.campaign.selectedEmail`).replace('{0}', lstEmailSuffix.length) : t(`item.campaign.selectEmail`) )
        }
        </Button>)
    }

    return (
        <Fragment>
            {renderButton()}
            <Modal title={ t(`item.campaign.selectEmail`) }
                   width={ 800 }
                   visible={ visible }
                   onOk={ handleOk }
                   onCancel={ handleCancel }
                   maskClosable={ false }
                   okButtonProps={{ disabled: (!canEdit || isFetchParseExcel) }}
            >
                
                <Wrapper className="m-b">
                    <Radio.Group 
                        value={typeData} 
                        onChange={ e => setTypeData(e.target.value) }
                    >
                        <Radio key='email' value='SPECIFIC' className="mr-0">Từng email</Radio>
                        <Radio key='emailSuffix' value='EMAIL_SUFFIX' className="mr-0">Đuôi email</Radio>
                    </Radio.Group>
                </Wrapper>
                
                <LoadingComponent spinning={ isFetchParseExcel }>
                    <div className="import-guest-modal-container">
                        <div className="guest-email-container">
                            <Input type={ `text` }
                                   className={ `form-control` }
                                   name={ `email` }
                                   value={ checkUndefine(emailInput) }
                                   autoComplete="off"
                                   onChange={ onChangeValue }
                                   placeholder={typeData === CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.SPECIFIC.id ? "email1@example.com, email2@example.com" : "@example.com, @gmail.com"}
                                   onPressEnter={ addEmailToList }
                            />
                            <Button disabled={ emailInput === undefined || emailInput === '' || !canEdit }
                                    type="primary ml-2 same-height-with-textbox"
                                    onClick={ addEmailToList }
                            >{ t(`action.add`) }</Button>
                            { 
                                typeData && typeData === CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.SPECIFIC.id && 
                                <Upload
                                    className="ml-2"
                                    customRequest={ handleChange }
                                    showUploadList={ false }
                                    action=""
                                >
                                    <Button className="same-height-with-textbox" disabled={ !canEdit }>
                                        <Icon type="upload"/>
                                    </Button>
                                </Upload>
                            }
                        </div>
                        <Divider orientation={ "left" }/>
                        <div className={ emailError && emailError.errors ? 'emails-list-container' : 'emails-list-container-without-error' }>
                            <List
                                size="small"
                                bordered
                                dataSource={ typeData === CAMPAIGN.OBJECT_TYPE_DATA_EMAIL.SPECIFIC.id ? emails : emailSuffix }
                                renderItem={ item => (
                                    <List.Item
                                        actions={ [
                                            <Icon type="close-circle" className="fs18 cursorPointer" onClick={ () => onRemoveEmail(item) }/>
                                        ] }
                                    >{ item }</List.Item>
                                ) }
                            />
                        </div>
                        {
                            emailError && emailError.validationError && emailError.errors && emailError.errors.length > 0 &&
                            <div className="guest-email-error-contain">
                                <div className={ `import-faild-title` }><h3>{ capitalizeFirstLetter(t('item.campaign.importFailed')) } </h3></div>
                                <div>{ capitalizeFirstLetter(t('item.campaign.totalRowInFile')) } { emailError.validationError.totalCount }</div>
                                <div>{ capitalizeFirstLetter(t('item.campaign.totalRowValid')) } { emailError.validationError.successCount }</div>
                                <div>{ capitalizeFirstLetter(t('item.campaign.totalRowInvalid')) } { emailError.validationError.errorCount }</div>
                                <div className="email-list-error-container">
                                    <List
                                        size="small"
                                        bordered
                                        dataSource={ emailError && emailError.errors }
                                        renderItem={ item => (
                                            <List.Item>
                                                <Typography.Text mark>[{ t(`item.campaign.importFailedRow`) } { item.row }]
                                                    [{ t(`item.campaign.importFailedError`) } { item.error }]</Typography.Text> { item.data }
                                            </List.Item>
                                        ) }
                                    />
                                </div>
                            </div>
                        }
                    </div>
                </LoadingComponent>
            </Modal>
        </Fragment>
    )
}

export default GuestEmails;
